import { Chip } from "./Chip";
import { SectionCard } from "./SectionCard";
import { RuleSquare, toneFromRuleRow } from "./RuleSquare";
import {
  CONDITION_ACCEPT_DISCLOSURE,
  INSUFFICIENCY_REASON_EXPLANATIONS,
  RULE_PLAIN_DESCRIPTIONS,
  RULE_RELIEVED_EXPLANATION,
  RULE_RESULT_DISPLAY,
  RULE_RESULT_EXPLANATIONS,
  RULE_STANDING_DISPLAY,
  RULE_STANDING_EXPLANATION,
  RULE_STANDING_VERIFYING_DISPLAY,
  RULE_STANDING_VERIFYING_EXPLANATION,
  RULE_WARNING_NAMES,
} from "../vocabulary";
import { fmtDate } from "../format";
import type { ConditionRow, RuleId, RuleRow } from "../types";

/**
 * The six health rules — the fleet row's squares, opened up. The card header
 * carries the same R1–R6 strip the fleet table shows (so the row an operator
 * clicked through on is the first thing they recognize here), and each rule
 * below leads with its own square, then says what it watches, what it read,
 * and since when. Five judge the tracking; Rule 6 (the click floor, ADR 0039)
 * is the Ads-side one — it judges whether the account delivered clicks at
 * all, and its failure sends the operator to the Google Ads account rather
 * than to a tag.
 *
 * The encoding rules, unchanged from the flat list they replace:
 * - **`standingWarning` outranks `result`.** A rule that fired reads `Failing`
 *   (red) until an admin resolves the incident — a passing evaluation is not a
 *   recovery (Rule 2 cannot re-fire once its baseline absorbs the cell; a fix
 *   applied resets the TDSD and blinds the windowed rules). Today's verdict is
 *   demoted to a gray sub-line, never dropped.
 * - Blind window (Rules 4–5 post-fix): the record's own evidence line IS the
 *   canonical copy — rendered in place of a chip, never green.
 * - A rule held by an accepted condition renders the condition tag, and WHY a
 *   human accepted it, in the open. Such a rule never carries a standing
 *   warning, so the tag and the red chip can never compete.
 *
 * This card is also the ONLY place a rule-scoped accepted condition can be
 * created. The accept action used to exist solely on the incident's problem
 * cards, so the one condition class the fleet actually carries — the Rule 3
 * relief on five census hotels — was reachable from the seeding pass and the
 * API and nowhere else. It belongs on the rule row because that is where the
 * failing evidence an operator is deciding about already is.
 */

const ALL_RULES: RuleId[] = ["1", "2", "3", "4", "5", "6"];

export function HealthRulesCard({
  rules,
  conditionForRule,
  onAcceptCondition,
  className,
}: {
  rules: RuleRow[];
  /** The active condition relieving a rule, resolved by the page. */
  conditionForRule: (rule: RuleRow) => ConditionRow | undefined;
  /** Opens the accept-as-condition modal for this rule. Omitted = read-only. */
  onAcceptCondition?: (rule: RuleRow) => void;
  className?: string;
}) {
  const byRule = new Map(rules.map((rule) => [rule.rule, rule]));
  return (
    <SectionCard
      title="Health rules"
      className={className}
      headerRight={
        <span className="inline-flex items-center gap-1">
          {ALL_RULES.map((ruleId) => {
            const rule = byRule.get(ruleId);
            return (
              <RuleSquare
                key={ruleId}
                rule={ruleId}
                tone={rule ? toneFromRuleRow(rule) : "none"}
                title={summaryTitle(ruleId, rule)}
              />
            );
          })}
        </span>
      }
    >
      <div className="-mx-2">
        {ALL_RULES.map((ruleId) => {
          const rule = byRule.get(ruleId);
          return rule ? (
            <HealthRuleRow
              key={ruleId}
              rule={rule}
              condition={conditionForRule(rule)}
              onAcceptCondition={onAcceptCondition}
            />
          ) : (
            <MissingRuleRow key={ruleId} ruleId={ruleId} />
          );
        })}
      </div>
    </SectionCard>
  );
}

/** The header square's hover: what the rule watches and where it stands. */
function summaryTitle(ruleId: RuleId, rule: RuleRow | undefined): string {
  const head = `Rule ${ruleId} — ${RULE_WARNING_NAMES[ruleId]}`;
  const watches = `Watches: ${RULE_PLAIN_DESCRIPTIONS[ruleId]}.`;
  if (!rule) return `${head}: never evaluated yet\n${watches}`;
  if (rule.mapsToAcceptedCondition)
    return `${head}: relieved by an accepted condition\n${watches}`;
  if (rule.standingWarning)
    return `${head}: ${
      rule.standingWarning.unresolved
        ? RULE_STANDING_DISPLAY.toLowerCase()
        : RULE_STANDING_VERIFYING_DISPLAY.toLowerCase()
    } since ${fmtDate(rule.standingWarning.firstDetectedAt)}\n${watches}`;
  return `${head}: ${RULE_RESULT_DISPLAY[rule.result].toLowerCase()} · evaluated ${fmtDate(rule.evaluatedAt)}\n${watches}`;
}

function RowShell({
  ruleId,
  tone,
  title,
  right,
  children,
}: {
  ruleId: RuleId;
  tone: React.ComponentProps<typeof RuleSquare>["tone"];
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 px-2 py-2.5 rounded-md border-b border-[#E9E9EB] last:border-b-0 hover:bg-[#FAFAFB] transition-colors">
      <span className="pt-0.5">
        <RuleSquare rule={ruleId} tone={tone} size="md" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <span
            className="text-sm font-medium text-content-primary"
            data-tip={`Watches: ${RULE_PLAIN_DESCRIPTIONS[ruleId]}.`}
          >
            {title}
          </span>
          {right}
        </div>
        {children}
      </div>
    </div>
  );
}

export function HealthRuleRow({
  rule,
  condition,
  onAcceptCondition,
}: {
  rule: RuleRow;
  condition?: ConditionRow;
  onAcceptCondition?: (rule: RuleRow) => void;
}) {
  const standing = rule.standingWarning;
  // Offered on anything that isn't cleanly passing — a green rule has nothing
  // to tolerate, and offering acceptance on all six would bury the two that
  // matter. Gated on the resolved CONDITION, never on the record's
  // `mapsToAcceptedCondition` flag: that flag is stamped at evaluation time,
  // so a condition accepted this afternoon does not appear on any record until
  // tomorrow's 04:30 run, and gating on it would leave the button live for a
  // day pointing at a 409.
  const canAccept =
    !!onAcceptCondition && !condition && (!!standing || rule.result !== "pass");
  return (
    <RowShell
      ruleId={rule.rule}
      tone={toneFromRuleRow(rule)}
      title={rule.title}
      right={
        <span className="flex items-center gap-2 shrink-0">
          {canAccept && (
            <button
              onClick={() => onAcceptCondition!(rule)}
              data-tip={CONDITION_ACCEPT_DISCLOSURE}
              className="text-xs font-medium text-gray-500 hover:text-content-primary whitespace-nowrap"
            >
              Accept as condition…
            </button>
          )}
          {ruleChip(rule, standing)}
        </span>
      }
    >
      {/* The failure's own sentence leads while the warning stands; the
          record's line leads otherwise. */}
      <div className="text-xs text-gray-500 mt-1">
        {standing ? standing.evidence : rule.evidence}
        {/* The reason ships raw and renders raw; only the two ADR 0039
            reasons carry a hover, because each misreads as a different fact
            (`low-volume` as a state, `rule-disabled` as a lock). */}
        {!standing && !rule.blindWindow && rule.insufficiencyReason && (
          <span
            className="text-gray-400"
            data-tip={INSUFFICIENCY_REASON_EXPLANATIONS[rule.insufficiencyReason]}
          >
            {" "}
            · {rule.insufficiencyReason}
          </span>
        )}
        {!standing &&
          !rule.blindWindow &&
          rule.result === "insufficient" &&
          !rule.insufficiencyReason && (
            <span className="text-gray-400">
              {" "}
              · {RULE_RESULT_EXPLANATIONS.insufficient}
            </span>
          )}
        {!standing && rule.periodFrom && rule.periodTo && (
          <span className="text-gray-400">
            {" "}
            · {rule.periodFrom} – {rule.periodTo}
          </span>
        )}
      </div>
      {standing && (
        <div className="text-xs text-gray-400 mt-0.5">
          Failing since {fmtDate(standing.firstDetectedAt)}
          {standing.standingDays != null && ` · ${standing.standingDays}d`}
          {standing.timesSeen > 1 && ` · seen ${standing.timesSeen}×`}
          {" · latest read "}
          {fmtDate(rule.evaluatedAt)}:{" "}
          {rule.blindWindow
            ? rule.evidence
            : `${RULE_RESULT_DISPLAY[rule.result].toLowerCase()} — ${rule.evidence}`}
          {rule.periodFrom && rule.periodTo && (
            <>
              {" "}
              ({rule.periodFrom} – {rule.periodTo})
            </>
          )}
        </div>
      )}
      {/* Shown on the CONDITION, not on the record's flag. The two diverge for
          up to a day after someone accepts one — the flag is stamped at
          evaluation time — and in that window the record's silence would read
          as "nothing happened" to the operator who just accepted it. The
          pending line says which of the two they are looking at. */}
      {condition && (
        <div className="text-xs text-gray-500 mt-0.5">
          Accepted because ({condition.reasonCategory}): {condition.reason}
          {condition.exitCondition && (
            <span className="text-gray-400">
              {" "}
              · exit: {condition.exitCondition}
            </span>
          )}
          {!rule.mapsToAcceptedCondition && (
            <span className="text-gray-400">
              {" "}
              · takes effect at the next daily evaluation; today's record
              predates it
            </span>
          )}
        </div>
      )}
    </RowShell>
  );
}

/** The row's right-hand verdict, in the encoding order the card documents:
 *  relieved outranks standing, standing outranks today's result, and a blind
 *  window renders no chip at all (its evidence line is the canonical copy). */
function ruleChip(rule: RuleRow, standing: RuleRow["standingWarning"]) {
  return (
    <>
      {rule.mapsToAcceptedCondition ? (
        <Chip tone="blue" title={RULE_RELIEVED_EXPLANATION}>
          auto-closed · accepted condition
        </Chip>
      ) : standing ? (
        <Chip
          tone={standing.unresolved ? "red" : "blue"}
          title={
            standing.unresolved
              ? RULE_STANDING_EXPLANATION
              : RULE_STANDING_VERIFYING_EXPLANATION
          }
        >
          {standing.unresolved
            ? RULE_STANDING_DISPLAY
            : RULE_STANDING_VERIFYING_DISPLAY}
        </Chip>
      ) : rule.blindWindow ? null : (
        <Chip
          tone={
            rule.result === "pass"
              ? "green"
              : rule.result === "fail"
                ? "red"
                : "neutral"
          }
          title={RULE_RESULT_EXPLANATIONS[rule.result]}
        >
          {RULE_RESULT_DISPLAY[rule.result]}
        </Chip>
      )}
    </>
  );
}

/** A rule with no evaluation record yet is absent from the payload — shown
 *  honestly, never as a pass. */
export function MissingRuleRow({ ruleId }: { ruleId: RuleId }) {
  return (
    <RowShell
      ruleId={ruleId}
      tone="none"
      title={RULE_WARNING_NAMES[ruleId]}
      right={<span className="text-xs text-gray-400">no evaluation yet</span>}
    >
      <div className="text-xs text-gray-400 mt-1">
        {RULE_PLAIN_DESCRIPTIONS[ruleId]}
      </div>
    </RowShell>
  );
}
